import { ContentLangToggle } from '@/features/content-lang-toggle'
import { ConfigStore, LangStore } from '@/shared/model/store'
import { Button, Card, Checkbox, Label, Preloader } from '@/shared/ui'
import { useLocation, useNavigate } from 'react-router-dom'
import { downloadFileFromUrl, getContentFromYaml, getFileList } from '../api/get-content-from-yaml'
import { ContentType } from '@/entities/feed-block/model/types'
import { ContentForm } from './form'
import { FormProvider, useForm } from 'react-hook-form'
import { formSchema, getDefaultValues } from '../model/model'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Blocker } from './blocker'
import { useContentLang } from '../lib/hook/use-content-lang'
import { useEffect, useState } from 'react'
import { createCid, validatePostData } from '../lib/utils'
import useRequest, { ApiUrlEnum } from '@/shared/lib/use-request'
import { createPostBody } from '../lib/create-post-body'
import useNotifier, { createCalls } from '@/shared/lib/use-notifier'
import { ContractsStore } from '@/shared/model/store/contracts-store'
import { PostCover } from './cover'
import { FileDrawer } from './drawer'
import { useCheckStaking } from '../lib/hook/use-check-staking'
import StakeModal from '@/pages/profile/ui/wallet/modals/stake-modal'
import { UserStore } from '@/entities/user'
import { SponsorsSumm } from './sponsors-summ'
import { stringToBytes32 } from '@/shared/lib'
import { ContentStore } from '@/entities/feed-block/model/content-store'
import useFundraise from '@/features/fundraise-block/ui/use-fundraise'
import { FundraiseCard, TFundraise } from '@/pages/profile/ui/wallet/fundraise'
import { GuardButton } from '@/features/guard-button'
import NpoModal from './npo-modal'
import FundraiseModal from './fundrise-modal'
import { XIcon } from 'lucide-react'
import {
	deleteDirectory,
	deleteFileByPath,
	getFileFromFolder,
	OPFSUpload,
	saveFileToFolder
} from '@/entities/opfs/utils'
import { createCommentBody } from '@/widgets/post/lib/utils'
import useIpfs from '@/entities/ipfs/use-ipfs'

type Props = {
	contentType: 'post' | 'comment' | 'post-edit' | 'comment-edit'
}

export const ContentEditor: React.FC<Props> = ({ contentType }) => {
	const { fetch: fetchIpfsUserDir, isLoading: isLoadingUserDir } = useRequest('storeDir')
	const { fetchRequest } = useRequest<{ list: ContentType[] }>('getContentList')
	const { gateway, loaded } = useIpfs()

	const { setActivePost } = ContentStore()
	const { domain, baseUrl } = ConfigStore()
	const {
		userData: { address },
		user_npos,
		signatureAddress
	} = UserStore()
	const { lang, langPack } = LangStore()

	const { state } = useLocation() as { state: ContentType | null }

	const { contentLang, setContentLang, contentLocales } = useContentLang({ lang, state })
	const { isLowStake, refetch, minStake } = useCheckStaking({ address: signatureAddress })
	const { getFundRiseData } = useFundraise()
	const navigate = useNavigate()

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: async () => getDefaultValues(contentLocales, contentType)
	})

	const [loading, setLoading] = useState(false)
	const [isNpo, setIsNpo] = useState(false)
	const [saveForm, setSaveForm] = useState(null)
	const [fundraise, setFundrise] = useState<TFundraise>({} as TFundraise)
	const [validated, setValidated] = useState(false)

	const { isComment } = form.getValues()

	const { watch } = form
	const fd = watch()

	const onRemoveForm = async () => {
		form.reset()
		await deleteDirectory(pathToFolder)
		await deleteFileByPath(`${pathToFolder}/tmp.json`)
	}

	const isEdit = ['post-edit', 'comment-edit'].includes(contentType)
	const pathToFolder = isEdit ? 'editor' : contentType

	const { contracts } = ContractsStore()

	const {
		writeContract,
		writeContractMulticall,
		isLoading: isLoadingWrite,
		isConfirmed
	} = useNotifier({
		onSuccess: async () => {
			setActivePost({} as ContentType)
			onRemoveForm()
			contentType === 'post' ? navigate('/new', { state: { from_new: true } }) : navigate(-1)
		}
	})

	const updateForm = async (res, comment?) => {
		const { locales } = form.getValues()
		const updateLocales = Object.keys(res.locales).reduce((acc, key) => {
			if (Object.prototype.hasOwnProperty.call(locales, key)) {
				if (state && state?.changed && comment && state?.changed[key]) {
					acc[key] = { ...res.locales[key], data: state.changed[key] }
				} else {
					acc[key] = res.locales[key]
				}
			}
			return acc
		}, {})

		setSaveForm({
			...form.getValues(),
			locales: {
				...locales,
				...updateLocales
			}
		})
		Object.keys(updateLocales).forEach(lang => {
			form.setValue(`locales.${lang}`, updateLocales[lang])
		})
		form.setValue('isNsfw', res.nsfw)
		form.setValue('thumbnail', res.thumbnail)
		if (Number(res.fundraiser) > 0) {
			const fundraise_get: TFundraise = await getFundRiseData(Number(res.fundraiser))
			setFundrise(fundraise_get?.target_amount > 0 ? fundraise_get : ({} as TFundraise))
		}
	}

	const isLoading = isLoadingWrite || isLoadingUserDir
	const getUserYaml = async () => {
		const comment = state?.savva_content.content_type === 'comment' ? true : false
		setLoading(true)
		const url = gateway + state?.ipfs
		const res = await getContentFromYaml(url + `/`, comment)
		const fl = await getFileList(baseUrl + ApiUrlEnum.ipfsLs + '/' + state?.ipfs)
		const uploads = fl.find(f => f.Name === 'uploads')

		if (uploads) {
			const dirList = await getFileList(baseUrl + ApiUrlEnum.ipfsLs + '/' + uploads.Hash)
			for (let i = 0; i < dirList.length; i++) {
				const file = await downloadFileFromUrl(`${gateway}${state?.ipfs}/uploads/${dirList[i].Name}`)
				await saveFileToFolder(`${pathToFolder}/uploads`, file, dirList[i].Name)
			}
		}

		if (res) {
			await updateForm(res, comment)
		}

		setLoading(false)
	}

	const getTemporaryData = async () => {
		const file = await getFileFromFolder(`${pathToFolder}/tmp.json`)
		const json = (await file?.text()) || ''
		const res = JSON.parse(json)

		try {
			const reg = await getFileFromFolder(`${pathToFolder}/reg_data.json`)

			if (reg) {
				const json_reg = await reg.text()
				const reg_data = JSON.parse(json_reg)
				if (reg_data.hash) {
					const result = await fetchRequest({
						params: {
							domain,
							savva_cid: reg_data.hash,
							show_nsfw: true
						}
					})
					if (result?.list?.length) {
						onRemoveForm()
						return
					}
				}
			}
		} catch (error) {
			console.log('error', error)
		}

		if (res) {
			await updateForm(res)
		}
	}

	const onSubmit = async (npo_address?: `0x${string}`) => {
		const isFormValid = await form.trigger()

		if (!address || !isFormValid) return
		const rd = {
			parent_savva_cid: state?.savva_cid,
			root_savva_cid: state?.savva_content.root_savva_cid ? state?.savva_content.root_savva_cid : state?.savva_cid
		}
		const currentAddress = npo_address || address

		const data = isComment ? rd : null

		if (isComment) {
			await createCommentBody(state, isEdit, '', form)
		} else {
			await createPostBody({
				commentData: data,
				address: currentAddress,
				form,
				pathToFolder,
				fundraiser: fundraise.id
			})
		}

		const computed_data = await OPFSUpload(pathToFolder)

		fetchIpfsUserDir({
			data: computed_data,
			isMultipart: true,
			onSuccess: res => {
				regContent(res.cid, npo_address)
			}
		})
	}

	const regContent = async (cid: string, npo_address?: `0x${string}`) => {
		if (!address) return

		const currentAddress = npo_address || address

		const reg_data = createCid(currentAddress, domain)

		const tmp_json = JSON.stringify(reg_data)
		const blob = new Blob([tmp_json], { type: 'application/json' })
		const info = new File([blob], `reg_data.json`)
		await saveFileToFolder(pathToFolder, info, 'reg_data.json')

		const call = {
			address: contracts.ContentRegistry.address,
			abi: contracts.ContentRegistry.abi,
			functionName: 'reg',
			args: [
				domain,
				isEdit ? state?.author.address : currentAddress,
				isEdit || form.getValues().isRegisterNewPost ? state?.guid : reg_data.uid,
				cid,
				stringToBytes32(contentType)
			]
		}

		if (state?.author.is_npo && isEdit) {
			writeContractMulticall(state?.author.address, createCalls([call]))
			return
		}

		if (npo_address && isEdit) {
			writeContractMulticall(npo_address, createCalls([call]))
			return
		}

		writeContract(call)
	}

	useEffect(() => {
		if(!loaded) return
		switch (pathToFolder) {
			case 'post':
				getTemporaryData()
				break
			case 'editor':
				getUserYaml()
				break
			default:
				break
		}
	}, [loaded])

	useEffect(() => {
		const { isValid } = validatePostData(fd, isComment)
		const tmp_json = JSON.stringify(fd)
		const blob = new Blob([tmp_json], { type: 'application/json' })
		const info = new File([blob], `tmp.json`)
		saveFileToFolder(pathToFolder, info, 'tmp.json')
		setValidated(isValid)
	}, [fd])

	if (form.formState.isLoading || loading) return <Preloader />

	return (
		<Card className='p-4'>
			<FormProvider {...form}>
				<div className='flex items-start justify-between'>
					<div>
						<PostCover form={form} path={pathToFolder} />
						<FileDrawer form={form} pathToFolder={pathToFolder} contentLang={contentLang} />
					</div>
					{isLowStake && (
						<div className='mt-12'>
							<p className='mb-4 text-sm text-destructive'>
								{langPack('editor.increase_stake')} {minStake} SAVVA
							</p>
							<StakeModal getData={refetch} />
						</div>
					)}
					{contentType === 'post' && <SponsorsSumm />}
					<div className='flex flex-row gap-4'>
						{contentLocales?.length > 1 && (
							<ContentLangToggle locales={contentLocales} value={contentLang} setValue={setContentLang} />
						)}
					</div>
				</div>
				<ContentForm
					contentLang={contentLang}
					setContentLang={setContentLang}
					form={form}
					pathToFolder={pathToFolder}
				/>
			</FormProvider>
			<div className='mt-5 flex flex-col gap-4'>
				{!isComment && fundraise && !fundraise.id && <FundraiseModal onSelect={setFundrise} selected={fundraise} />}
				{fundraise && fundraise.id && (
					<div className='flex w-full flex-col'>
						<Label>{langPack('fundraise.fundraise')}</Label>
						<div className='relative mt-3'>
							<FundraiseCard el={fundraise} />
							<Button
								className='absolute right-14 top-[9px] z-50'
								onClick={() => setFundrise({} as TFundraise)}
								size={'icon'}
							>
								<XIcon />
							</Button>
						</div>
					</div>
				)}
				{user_npos?.length > 0 && !isEdit && (
					<div className='flex items-center space-x-2'>
						<Checkbox onCheckedChange={setIsNpo} checked={isNpo} />
						<Label className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'>
							{langPack('editor.npo_registration')}
						</Label>
					</div>
				)}
				<div className='mt-5 flex flex-row gap-4'>
					{isNpo ? (
						<NpoModal
							reg={onSubmit}
							isLoading={isLoading || isLoadingWrite}
							staked={5000}
							disabledButton={isLowStake || isLoading || !validated}
						/>
					) : (
						<GuardButton
							onClick={() => onSubmit()}
							title={langPack(`action.${contentType === 'comment' || contentType === 'post' ? 'create' : 'update'}`)}
							requireAuth
							disabled={isLowStake || isLoading || !validated}
							isLoading={isLoading}
						/>
					)}
					<Button onClick={onRemoveForm} disabled={isLoading} variant={'ghost'}>
						{langPack('action.clear')}
					</Button>
				</div>
			</div>
			<Blocker state={saveForm} form={form} isConfirmed={isConfirmed} />
		</Card>
	)
}
